import DateUtils, {DateUtilsTypes} from '../../../../core/services/date-utils';
import {AdvancedDateRangeValue, CommitDateRangeArg, DateRangeBatch, DateRangeInputMode, DateRangeType, StaticDateRangeValue} from './types';
import {PredefinedDateRanges} from "./PredefinedDateRanges";

/**
 * Utility class for computing and displaying date ranges.
 * All methods operate on CommitDateRangeArg or its sub-shapes.
 */
export class DateRangeUtils {

    // =========================================================================
    // Internal helpers
    // =========================================================================

    /**
     * Ensures chronological order of a [start, end] pair.
     * If the user has the dates inverted, silently swap them.
     */
    private static chronological(
        pair: [DateUtilsTypes.Instance, DateUtilsTypes.Instance]
    ): [DateUtilsTypes.Instance, DateUtilsTypes.Instance] {
        const [a, b] = pair;
        return a.isBefore(b) ? [a, b] : [b, a];
    }

    // =========================================================================
    // Public dispatcher methods (branch on mode)
    // =========================================================================

    /**
     * Resolves a CommitDateRangeArg to a [start, end] date pair.
     * Dispatches to the appropriate mode-specific implementation.
     */
    static getDateRange(arg: CommitDateRangeArg, inputMode?: DateRangeInputMode): [DateUtilsTypes.Instance | undefined, DateUtilsTypes.Instance | undefined] | undefined {
        // 1. Check for predefined shortcut first
        if (arg.predefinedId) {
            const option = PredefinedDateRanges.allRanges.find(o => o.id === arg.predefinedId);
            if (option) return option.getDateRange();
        }

        // 2. Otherwise dispatch to mode
        switch (arg.mode ?? 'auto') {
            case 'auto':
                return DateRangeUtils.getAutoDateRange(arg);
            case 'advanced':
                return arg.advanced ? DateRangeUtils.getAdvancedDateRange(arg.advanced) : undefined;
            case 'static':
                return arg.static ? DateRangeUtils.getStaticDateRange(arg.static, inputMode) : undefined;
            default:
                return undefined;
        }
    }

    /**
     * Returns a human-readable label for the given CommitDateRangeArg.
     * Dispatches to the appropriate mode-specific implementation.
     */
    static displayDateRange(arg: CommitDateRangeArg, inputMode?: DateRangeInputMode): string | undefined {
        // 1. Check for predefined shortcut first
        if (arg.predefinedId) {
            const option = PredefinedDateRanges.allRanges.find(o => o.id === arg.predefinedId);
            if (option) {
                const group = PredefinedDateRanges.allGroups[option.group ?? ''];
                if (group) {
                    return `${group.title}: ${option.title}`;
                }
                return option.title;
            }
        }

        // 2. Otherwise dispatch to mode
        switch (arg.mode ?? 'auto') {
            case 'auto':
                return DateRangeUtils.displayAutoDateRange(arg);
            case 'advanced':
                return arg.advanced ? DateRangeUtils.displayAdvancedDateRange(arg.advanced) : undefined;
            case 'static':
                return arg.static ? DateRangeUtils.displayStaticDateRange(arg.static, inputMode) : undefined;
            default:
                return undefined;
        }
    }

    // =========================================================================
    // Auto mode
    // =========================================================================

    /**
     * Get the start and end dates based on the type, batch, and count (auto mode).
     *
     * @param arg The CommitDateRangeArg containing auto mode parameters.
     */
    static getAutoDateRange(arg: CommitDateRangeArg): [DateUtilsTypes.Instance, DateUtilsTypes.Instance] | undefined {
        const auto = arg.auto;
        if (!auto) return undefined;
        const {type, batch, count, untilNow, fromNow} = auto;
        if (!type || !batch) return undefined;
        const raw = DateRangeUtils._getAutoDateRangeRaw(type, batch, count, untilNow, fromNow);
        return raw ? DateRangeUtils.chronological(raw) : undefined;
    }

    /** Inner implementation that returns the raw (possibly un-ordered) pair. */
    private static _getAutoDateRangeRaw(
        type: string, batch: string, count: number | undefined,
        untilNow: boolean | undefined, fromNow: boolean | undefined
    ): [DateUtilsTypes.Instance, DateUtilsTypes.Instance] | undefined {
        switch (type) {
            case 'current': {
                switch (batch) {
                    case 'hour':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().startOf('hour'),
                            untilNow ? DateUtils.local() : DateUtils.local().endOf('hour')
                        ];
                    case 'day':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().endOf('day')
                        ];
                    case 'week':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().startOf('week').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().endOf('week').endOf('day')
                        ];
                    case 'month':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().startOf('month').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().endOf('month').endOf('day')
                        ];
                    case 'quarter': {
                        const currentMonth = DateUtils.local().month();
                        const quarterStartMonth = currentMonth - (currentMonth % 3);
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().month(quarterStartMonth).startOf('month').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().month(quarterStartMonth + 2).endOf('month').endOf('day')
                        ];
                    }
                    case 'year':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().startOf('year').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().endOf('year').endOf('day')
                        ];
                    default:
                        return undefined;
                }
            }
            case 'last': {
                if (!count || count < 1) return undefined;
                switch (batch) {
                    case 'hour':
                        return [
                            DateUtils.local().subtract(count, 'hours').startOf('hour'),
                            untilNow ? DateUtils.local() : DateUtils.local().subtract(1, 'hours').endOf('hour')
                        ];
                    case 'day':
                        return [
                            DateUtils.local().subtract(count, 'days').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().subtract(1, 'days').endOf('day')
                        ];
                    case 'week':
                        return [
                            DateUtils.local().subtract(count, 'weeks').startOf('week').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().subtract(1, 'weeks').endOf('week').endOf('day')
                        ];
                    case 'month':
                        return [
                            DateUtils.local().subtract(count, 'months').startOf('month').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().subtract(1, 'months').endOf('month').endOf('day')
                        ];
                    case 'quarter': {
                        const currentMonth = DateUtils.local().month();
                        const currentQuarterStartMonth = currentMonth - (currentMonth % 3);
                        const quarterStartMonth = currentQuarterStartMonth - count * 3;
                        return [
                            DateUtils.local().month(quarterStartMonth).startOf('month').startOf('day'),
                            untilNow
                                ? DateUtils.local()
                                : DateUtils.local().month(quarterStartMonth + (count - 1) * 3 + 2).endOf('month').endOf('day')
                        ];
                    }
                    case 'year':
                        return [
                            DateUtils.local().subtract(count, 'years').startOf('year').startOf('day'),
                            untilNow ? DateUtils.local() : DateUtils.local().subtract(1, 'years').endOf('year').endOf('day')
                        ];
                    default:
                        return undefined;
                }
            }
            case 'next': {
                if (!count || count < 1) return undefined;
                switch (batch) {
                    case 'hour':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().add(1, 'hours').startOf('hour'),
                            DateUtils.local().add(count, 'hours').endOf('hour')
                        ];
                    case 'day':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().add(1, 'days').startOf('day'),
                            DateUtils.local().add(count, 'days').endOf('day')
                        ];
                    case 'week':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().add(1, 'weeks').startOf('week').startOf('day'),
                            DateUtils.local().add(count, 'weeks').endOf('week').endOf('day')
                        ];
                    case 'month':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().add(1, 'months').startOf('month').startOf('day'),
                            DateUtils.local().add(count, 'months').endOf('month').endOf('day')
                        ];
                    case 'quarter': {
                        const currentMonth = DateUtils.local().month();
                        const quarterStartMonth = currentMonth - (currentMonth % 3) + 3;
                        return [
                            fromNow
                                ? DateUtils.local()
                                : DateUtils.local().month(quarterStartMonth).startOf('month').startOf('day'),
                            DateUtils.local().month(quarterStartMonth + (count - 1) * 3 + 2).endOf('month').endOf('day')
                        ];
                    }
                    case 'year':
                        return [
                            fromNow ? DateUtils.local() : DateUtils.local().add(1, 'years').startOf('year').startOf('day'),
                            DateUtils.local().add(count, 'years').endOf('year').endOf('day')
                        ];
                    default:
                        return undefined;
                }
            }
            default:
                return undefined;
        }
    }

    /**
     * Display a human-readable date range for auto mode.
     */
    static displayAutoDateRange(arg: CommitDateRangeArg): string | undefined {
        const auto = arg.auto;
        if (!auto) return undefined;
        const {type, batch, count, untilNow, fromNow} = auto;
        if (type === 'current') {
            if (!batch) return undefined;
            if (untilNow) {
                if (batch === 'hour') return 'This hour until now';
                if (batch === 'day') return 'Today until now';
                return `${batch} to date`;
            } else if (fromNow) {
                if (batch === 'hour') return 'This hour from now';
                if (batch === 'day') return 'Today from now';
                return `Rest of the ${batch}`;
            }
            if (batch === 'day') return 'Today';
            return `This ${batch}`;
        } else if (type === 'last') {
            if (!batch || !count) return undefined;
            let result: string;
            if (batch === 'day' && count === 1) {
                result = 'Yesterday';
            } else {
                result = ['Last', count === 1 ? '' : count, `${batch}${count > 1 ? 's' : ''}`].filter(Boolean).join(' ');
            }
            if (untilNow) return `${result} until now`;
            return result;
        } else if (type === 'next') {
            if (!batch || !count) return undefined;
            let result: string;
            if (batch === 'day' && count === 1) {
                result = 'Tomorrow';
            } else {
                result = ['Next', count === 1 ? '' : count, `${batch}${count > 1 ? 's' : ''}`].filter(Boolean).join(' ');
            }
            if (fromNow) return `${result} from now`;
            return result;
        }
        return undefined;
    }

    // =========================================================================
    // Advanced mode
    // =========================================================================

    /**
     * Resolves an AdvancedDateRangeValue to a [start, end] date pair.
     * Both endpoints are computed as offsets from now and normalized to semantic boundaries.
     */
    static getAdvancedDateRange(v: AdvancedDateRangeValue): [DateUtilsTypes.Instance | undefined, DateUtilsTypes.Instance | undefined] | undefined {
        const {startCount, startBatch, startType, startFromNow, endCount, endBatch, endType, endFromNow} = v;

        const startInvalid = (!startFromNow && (!startCount || startCount < 1 || !startBatch));
        const endInvalid = (!endFromNow && (!endCount || endCount < 1 || !endBatch));
        if (startInvalid && endInvalid) return undefined;

        const start = DateRangeUtils._computeAdvancedPoint(startFromNow, startCount, startBatch, startType, true);
        const end = DateRangeUtils._computeAdvancedPoint(endFromNow, endCount, endBatch, endType, false);

        if (!start && !end) return undefined;

        if (!start) return [undefined, end!]; // If start is missing, treat as single point range at end
        if (!end) return [start, undefined]; // If end is missing, treat as single point range at start

        // Ensure chronological order
        return this.chronological([start, end]);
    }

    /**
     * Internal helper to compute a semantic point for advanced mode.
     * @param isNow If true, returns current moment (Now).
     * @param count Number of units.
     * @param batch Unit (day, week, month, etc.).
     * @param type Direction (last/next).
     * @param isStart If true, normalizes to startOf unit; otherwise endOf unit.
     */
    private static _computeAdvancedPoint(
        isNow: boolean | undefined,
        count: number | undefined,
        batch: DateRangeBatch | undefined,
        type: DateRangeType | undefined,
        isStart: boolean
    ): DateUtilsTypes.Instance | undefined {
        if (isNow) return DateUtils.local();
        if (!count || !batch) return undefined;

        const effectiveType = type ?? 'last';
        const unitMap: Record<string, string> = {
            hour: 'hours', day: 'days', week: 'weeks', month: 'months', year: 'years'
        };

        let d = DateUtils.local();
        if (batch === 'quarter') {
            d = effectiveType === 'next' ? d.add(count * 3, 'months') : d.subtract(count * 3, 'months');
        } else {
            d = effectiveType === 'next' ? d.add(count, unitMap[batch] as any) : d.subtract(count, unitMap[batch] as any);
        }

        // Apply semantic boundaries
        if (batch === 'quarter') {
            const month = d.month();
            const quarterStartMonth = month - (month % 3);
            const quarterD = d.month(quarterStartMonth).startOf('month').startOf('day');
            return isStart ? quarterD : quarterD.add(2, 'months').endOf('month').endOf('day');
        }

        switch (batch) {
            case "hour":
                return isStart
                    ? d.startOf(batch as any)
                    : d.endOf(batch as any);
            default:
                return isStart
                    ? d.startOf(batch as any).startOf('day')
                    : d.endOf(batch as any).endOf('day');
        }
    }

    /**
     * Returns a human-readable label for advanced mode.
     * e.g. "60 days ago → 30 days ago"
     */
    static displayAdvancedDateRange(v: AdvancedDateRangeValue): string | undefined {
        const {startCount, startBatch, startType, startFromNow, endCount, endBatch, endType, endFromNow} = v;

        const startInvalid = (!startFromNow && (!startCount || startCount < 1 || !startBatch));
        const endInvalid = (!endFromNow && (!endCount || endCount < 1 || !endBatch));
        if (startInvalid && endInvalid) return undefined;

        const formatPoint = (isNow: boolean | undefined, count: number | undefined, batch: DateRangeBatch | undefined, type: DateRangeType | undefined): string | undefined => {
            if (isNow) return 'Now';
            if (!count || !batch || !type) return undefined;
            const plural = count && count > 1 ? 's' : '';
            const suffix = (type === 'next') ? 'ahead' : 'ago';
            return `${count} ${batch}${plural} ${suffix}`;
        };

        let startLabel = formatPoint(startFromNow, startCount, startBatch, startType);
        let endLabel = formatPoint(endFromNow, endCount, endBatch, endType);

        if (startLabel && endLabel) {
            // Resolve the actual timestamps to detect if the user's input is inverted
            const resolved = DateRangeUtils.getAdvancedDateRange(v);
            if (resolved) {
                // Compare the raw (intended) start point with the chronological start.
                // If they differ, the range was inverted and swapped.
                const [chronologicalStart] = resolved;
                const intendedStart = DateRangeUtils._computeAdvancedPoint(startFromNow, startCount, startBatch, startType, true);

                if (intendedStart && !intendedStart.isSame(chronologicalStart, 'minute')) {
                    // The start was actually after the end — swap labels to match display
                    [startLabel, endLabel] = [endLabel, startLabel];
                }
            }

            return `${startLabel} → ${endLabel}`;
        } else {
            if (startLabel) return `from ${startLabel}`;
            if (endLabel) return `until ${endLabel}`;
            return undefined;
        }
    }

    // =========================================================================
    // Static mode
    // =========================================================================

    /**
     * Resolves a StaticDateRangeValue to a [start, end] date pair.
     */
    static getStaticDateRange(v: StaticDateRangeValue, inputMode?: DateRangeInputMode): [DateUtilsTypes.Instance, DateUtilsTypes.Instance] | undefined {
        const {startDate, endDate} = v;
        if (!startDate || !endDate) return undefined;

        let start = DateUtils.local(startDate);
        let end = DateUtils.local(endDate);

        if (inputMode === 'date' || !inputMode) {
            start = start.startOf('day');
            end = end.endOf('day');
        }

        return DateRangeUtils.chronological([start, end]);
    }

    /**
     * Returns a human-readable label for static mode.
     * e.g. "Jan 1, 2025 – Dec 31, 2025"
     */
    static displayStaticDateRange(v: StaticDateRangeValue, inputMode?: DateRangeInputMode): string | undefined {
        const {startDate, endDate} = v;
        if (!startDate || !endDate) return undefined;
        const format = inputMode === 'datetime-local' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY';
        const [start, end] = DateRangeUtils.chronological([DateUtils.local(startDate), DateUtils.local(endDate)]);
        return `${start.format(format)} – ${end.format(format)}`;
    }

    // =========================================================================
    // Utility / legacy helpers
    // =========================================================================

    /**
     * Converts a predefined flag to a CommitDateRangeArg.
     */
    static toDateRangeFromDateRangeFlag(flag?: string): CommitDateRangeArg | undefined {
        switch (flag) {
            case 'lastYear':
                return {mode: 'auto', auto: {type: 'last', batch: 'year', count: 1}};
            case 'yearToDate':
                return {mode: 'auto', auto: {type: 'current', batch: 'year', untilNow: true}};
            case 'last90Days':
                return {mode: 'auto', auto: {type: 'last', batch: 'day', count: 90}};
            case 'last30Days':
                return {mode: 'auto', auto: {type: 'last', batch: 'day', count: 30}};
            default:
                return undefined;
        }
    }

    /**
     * Extracts or constructs a CommitDateRangeArg from legacy filters or existing dateRangeValue.
     */
    static getDateRangeValueFromLegacyFilters(filters: any): CommitDateRangeArg | undefined {
        if (!filters) return undefined;
        // 1. If dateRangeValue exists, parse if string, else return as is
        if (filters.dateRangeValue) {
            return typeof filters.dateRangeValue === 'string' ? JSON.parse(filters.dateRangeValue) : filters.dateRangeValue;
        }

        // 2. If useDateRange is true, construct from legacy auto properties
        if (filters.useDateRange && filters.dateRangeType) {
            return {
                mode: 'auto',
                auto: {
                    type: filters.dateRangeType,
                    batch: filters.dateRangeBatch,
                    count: filters.dateRangeCount,
                    untilNow: filters.dateRangeUntilNow,
                    fromNow: filters.dateRangeFromNow,
                }
            };
        }

        // 3. Otherwise if startDate and endDate exist, construct a static range
        if (filters.startDate && filters.endDate) {
            return {
                mode: 'static',
                static: {
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                }
            };
        }

        return undefined;
    }

    /** Capitalizes the first letter of a string */
    static capitalizeFirstLetter(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
